import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { Col, Button } from "react-bootstrap";
import { bool, func, string } from "prop-types";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as settingActions, selectors as settingsSelectors } from "reducers/settings";
import { actions as notificationActions } from "reducers/notification";
import { actions as layoutActions } from "reducers/layout";
import Container from "pages/_components/Container";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import * as i18n from "util/i18n";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import Image from "pages/_components/Image";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Heading from "pages/_components/Heading";
import MainContainer from "pages/_components/MainContainer";
import classNames from "classnames";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

const ACCOUNTS_OFFICER_SCOPES = ["accountsOfficer"];

class AccountsOfficer extends Component {
    static propTypes = {
        accountsOfficer: {
            accountsOfficerInformation: {
                name: string.isRequired,
                email: string.isRequired,
                telephone: string.isRequired,
            },
        }.isRequired,
        dispatch: func.isRequired,
        fetching: bool.isRequired,
        isDesktop: bool.isRequired,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        embeddedInV3Shell: false,
    };

    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        const { dispatch, embeddedInV3Shell } = this.props;
        dispatch(settingActions.getAccountsOfficerData());

        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatchV3ShellLayout(dispatch, {
                titleKey: "settings.index.accountsOfficer.label",
                goBackPath: "/settings",
                rightContentMobile: this.rightContent(),
                scopes: ACCOUNTS_OFFICER_SCOPES,
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Resyncronizamos el slot derecho del header del shell v3 cada vez que
        // cambian los datos del oficial: en el primer mount el fetch aún no
        // resolvió y `rightContentMobile` quedaría capturado con strings vacíos.
        const { dispatch, embeddedInV3Shell, accountsOfficer, isDesktop } = this.props;
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return;
        }

        const prevInfo = prevProps.accountsOfficer.accountsOfficerInformation;
        const currInfo = accountsOfficer.accountsOfficerInformation;
        const dataChanged =
            prevInfo.name !== currInfo.name ||
            prevInfo.email !== currInfo.email ||
            prevInfo.telephone !== currInfo.telephone;
        const isDesktopChanged = prevProps.isDesktop !== isDesktop;

        if (dataChanged || isDesktopChanged) {
            dispatch(layoutActions.setRightContentMobile(this.rightContent()));
        }
    }

    componentWillUnmount() {
        const { dispatch, embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }

    showNotification = () => {
        const { dispatch } = this.props;
        const scopes = isUxDashboardImprovementsEnabled() ? ACCOUNTS_OFFICER_SCOPES : ["settings"];
        dispatch(
            notificationActions.showNotification(i18n.get("settings.index.accountsOfficer.copied"), "success", scopes),
        );
    };

    getSettingsData = () => {
        const { accountsOfficer, isDesktop } = this.props;
        const { name, email, telephone } = accountsOfficer.accountsOfficerInformation;

        return (
            <>
                <Col
                    sm={12}
                    md={4}
                    className={`col col-12 d-flex align-items-center ${
                        isDesktop ? "justify-content-start" : "justify-content-center"
                    }`}>
                    <Image wrapperClassName="w-auto svg-wrapper " className="svg-key" src="images/account_circle.svg" />
                    <span className="data-text content-data-strong officer-name f-size-1 line-height-1 align-font-fix-02">
                        {name}
                    </span>
                </Col>
                <Col sm={12} md={4} className="col col-12 d-flex justify-content-center align-items-center">
                    <Heading.DataGroup
                        containerClassName="transfer-data data-wrapper-flex mb-0 justify-content-center mt-1"
                        dataClassName="f-size-4 font-light"
                        label="settings.index.accountsOfficer.mail.label"
                        data={email}
                    />
                </Col>
                <Col
                    sm={12}
                    md={4}
                    className={`col col-12 d-flex align-items-center ${
                        isDesktop ? "justify-content-right" : "justify-content-center"
                    }`}>
                    <Heading.DataGroup
                        containerClassName="transfer-data data-wrapper-flex mb-0 justify-content-end mt-1"
                        dataClassName="f-size-4 font-light"
                        label="settings.index.accountsOfficer.telephone.label"
                        data={telephone}
                    />
                </Col>
            </>
        );
    };

    getText = (name, email, telephone) => {
        const params = {
            ACCOUNTS_OFFICER_NAME: name,
            ACCOUNTS_OFFICER_EMAIL: email,
            ACCOUNTS_OFFICER_PHONE: telephone,
        };

        const message = i18n.get("settings.index.accountsOfficer.copied.data");
        return i18n.replaceParams(message, params);
    };

    isNativeShareAvailable = () => {
        const { isDesktop } = this.props;
        return !isDesktop && typeof window !== "undefined" && !!window.cordova && !!window.plugins?.socialsharing;
    };

    handleShareClick = () => {
        const { accountsOfficer } = this.props;
        const { name, email, telephone } = accountsOfficer.accountsOfficerInformation;
        const text = this.getText(name, email, telephone);

        if (this.isNativeShareAvailable()) {
            const options = {
                message: text,
                subject: i18n.get("settings.index.accountsOfficer.label"),
            };
            try {
                window.plugins.socialsharing.shareWithOptions(options, null, null);
                return;
            } catch (e) {
                // Si el plugin nativo falla, caemos al copy-to-clipboard como fallback.
            }
        }

        // Fallback: copiar al portapapeles + toast (web/desktop o cuando el
        // plugin nativo no está disponible).
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText && this.isNativeShareAvailable()) {
            navigator.clipboard.writeText(text).then(() => this.showNotification());
        }
    };

    rightContent = () => {
        const { accountsOfficer, isDesktop } = this.props;
        const { name, email, telephone } = accountsOfficer.accountsOfficerInformation;

        const buttonShare = (
            <Button
                bsStyle={isDesktop ? "outline" : "link"}
                id="editDashboardBtn"
                onClick={this.handleShareClick}
                className={isDesktop ? "cmf-button-width" : "no-shadow p-0 m-0"}>
                <Image
                    className="svg-icon-btn"
                    src={`images/share${!isDesktop ? "-white" : ""}.svg`}
                    styles={isDesktop ? { fontSize: "1.6rem" } : { position: "relative", fontSize: "1.6rem" }}
                />
                {isDesktop && (
                    <I18n id="global.share" componentProps={{ style: { marginLeft: "1rem", fontSize: "1rem" } }} />
                )}
            </Button>
        );

        // En desktop (o cuando no hay share nativo disponible) envolvemos en
        // CopyToClipboard para mantener el comportamiento previo de copiar
        // al portapapeles y mostrar el toast.
        return this.isNativeShareAvailable() ? (
            buttonShare
        ) : (
            <CopyToClipboard text={this.getText(name, email, telephone)} onCopy={() => this.showNotification()}>
                {buttonShare}
            </CopyToClipboard>
        );
    };

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(push("/desktop/"));
    };

    centerContentMobile = () => (
        <div className="title-account-header-multiline-background-blue">
            <h1 style={{ width: "100%", margin: "0.2rem" }}>{i18n.get("settings.index.accountsOfficer.label")}</h1>
        </div>
    );

    render() {
        const { accountsOfficer, fetching, isDesktop, embeddedInV3Shell } = this.props;
        const { name } = accountsOfficer.accountsOfficerInformation;
        const isShellActive = embeddedInV3Shell && isUxDashboardImprovementsEnabled();

        return (
            <>
                {!isShellActive && <Notification scopeToShow="settings" />}
                <div className="admin-detail-head px-0">
                    {isDesktop && <Head onBack={this.handleBack} />}
                    {(!isShellActive || isDesktop) && (
                        <Head
                            onBack={!isDesktop ? this.handleBack : null}
                            title={isDesktop ? "settings.index.accountsOfficer.label" : null}
                            rightContent={name && this.rightContent}
                            headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : " "}
                            centerElement={!isDesktop ? this.centerContentMobile : null}
                        />
                    )}
                </div>
                <PageLoading className="line-loader" loading={fetching}>
                    <div className="view-morphing">
                        <Container className="align-items-baseline flex-grow container-white py-4 mt-45 mb-3">
                            {this.getSettingsData()}
                        </Container>
                    </div>
                    <div className="view-morphing">
                        <MainContainer>
                            <div className="above-the-fold">
                                <Container className="flex-grow account-officer-info">
                                    <Col xl="5" lg="5" md="5" sm="12" className={classNames({ "px-0": isDesktop })}>
                                        <I18n
                                            componentProps={{ className: "font-light" }}
                                            id="settings.index.accountsOfficer.info.text"
                                            component="p"
                                        />
                                    </Col>
                                </Container>
                            </div>
                        </MainContainer>
                    </div>
                </PageLoading>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    accountsOfficer: settingsSelectors.getAccountsOfficer(state),
    fetching: settingsSelectors.isFetching(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountsOfficer);
